<script setup>
/**
 * The frame every settings page sits in.
 *
 * Text.com's settings pages share one shell: a fixed header carrying the title
 * on the left and the page's primary action on the right, over a body that
 * scrolls beneath it. Forms and toggle lists live in a narrow centred column;
 * lists and tables take the full width (`wide`).
 *
 * The header also owns saving. Tola departs from Text.com here - they autosave
 * every field - so `Save changes` lives in the action slot, disabled until the
 * page reports itself dirty. Keeping it in the fixed header means long forms do
 * not need a second sticky bar at the bottom.
 *
 * It also owns the `Learn more` link's white-label policy: pages hand over the
 * documentation URL and the shell suppresses it on custom-branded installs, so
 * no page repeats that guard.
 */
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  learnMoreHref: { type: String, default: '' },
  // Lists and tables run full-bleed; forms and toggle lists stay in the column.
  wide: { type: Boolean, default: false },
  showSave: { type: Boolean, default: false },
  isDirty: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['save']);

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);

const helpUrl = computed(() =>
  isACustomBrandedInstance.value ? '' : props.learnMoreHref
);
</script>

<template>
  <div class="flex flex-col w-full h-full min-w-0 font-inter">
    <header
      class="flex items-center flex-shrink-0 justify-between gap-4 h-14 px-6 border-b border-n-weak"
    >
      <h1 class="text-base font-medium truncate text-n-slate-12">
        {{ title }}
      </h1>
      <div class="flex items-center flex-shrink-0 gap-2">
        <slot name="actions" />
        <Button
          v-if="showSave"
          size="sm"
          :label="$t('SETTINGS_KIT.SAVE_CHANGES')"
          :disabled="!isDirty || isSaving"
          :is-loading="isSaving"
          @click="emit('save')"
        />
      </div>
    </header>

    <div class="flex-1 min-h-0 overflow-y-auto">
      <div
        class="flex flex-col w-full gap-6 px-6 py-6"
        :class="wide ? '' : 'max-w-2xl mx-auto'"
      >
        <p v-if="description" class="text-sm text-n-slate-11">
          {{ description }}
          <a
            v-if="helpUrl"
            :href="helpUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-blue-11 hover:underline"
          >
            {{ $t('SETTINGS_KIT.LEARN_MORE') }}
          </a>
        </p>
        <slot name="tabs" />
        <slot />
      </div>
    </div>
  </div>
</template>
